import { ErrorText } from './ErrorText';
import { RootState } from '../store/store';
import { useSelector } from 'react-redux';
import { OrderDetails, StoreInterface } from '../interfaces';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import AddressComponent from './AddressComponent';
import { useEffect, useState } from 'react';
import { formatCurrency } from '../utils';

interface CheckoutFormProps {
  orderDetails: OrderDetails;
  setOrderDetails: (details: OrderDetails) => void;
  orderDetailsValid: OrderDetails;
}

export default function CheckoutFormAlt({
  orderDetails,
  setOrderDetails,
  orderDetailsValid,
}: CheckoutFormProps) {
  const { landmarks } = useSelector((state: RootState) => state.purchase);
  const { user } = useSelector((state: RootState) => state.auth);
  const [store, setStore] = useState<StoreInterface | null>(null);
  const { storeId } = useSelector((state: RootState) => state.setup);
  const { stores } = useSelector((state: RootState) => state.products);

  const handleSelect = (
    coordinates: { address: string; lat: number; lon: number } | null
  ) => {
    if (coordinates) {
      setOrderDetails({
        ...orderDetails,
        line1: coordinates.address,
        lat: coordinates.lat,
        long: coordinates.lon,
      });
    }
  };

  useEffect(() => {
    if (storeId && stores) {
      const selectedStore = stores.find((s) => s.id === storeId);
      if (selectedStore) {
        setStore(selectedStore);
      }
    }
  }, [stores, storeId]);

  useEffect(() => {
    const isPickup = store?.isPickupEnabled;
    const isDelivery = store?.isDeliveryEnabled;

    if (isPickup !== isDelivery) {
      setOrderDetails({
        ...orderDetails,
        orderType: isPickup ? 0 : 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const cardClass =
    'bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm';
  const inputClass = `w-full font-dmsans px-[14px] py-[10px] border 
    border-secondary-50 rounded-lg text-[13px] bg-white 
    text-gray-800 outline-none focus:border-blue-400 
    transition-all placeholder-gray-400`;
  const labelClass = 'block text-[13px] font-semibold text-gray-700 mb-[6px]';

  return (
    <div className="w-full ">
      {/* Fulfillment Method */}
      <div className={cardClass}>
        <h2 className="text-[15px] font-bold text-gray-800 mb-4">
          Fulfillment Method
        </h2>

        {store?.isPickupEnabled || store?.isDeliveryEnabled ? (
          <div className="grid grid-cols-2 gap-3">
            {store?.isPickupEnabled && (
              <button
                type="button"
                onClick={() =>
                  setOrderDetails({ ...orderDetails, orderType: 0 })
                }
                className={`flex flex-col items-center justify-center py-4 px-3 rounded-xl border-2 transition-all cursor-pointer
                  ${
                    Number(orderDetails.orderType) === 0
                      ? 'border-blue-500 bg-blue-50 text-blue-600'
                      : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300'
                  }`}
              >
                <span className="text-[14px] font-semibold">Pickup</span>
                <span className="text-[12px] mt-1 font-normal">Free</span>
              </button>
            )}
            {store?.isDeliveryEnabled && (
              <button
                type="button"
                onClick={() =>
                  setOrderDetails({ ...orderDetails, orderType: 1 })
                }
                className={`flex flex-col items-center justify-center py-4 px-3 rounded-xl border-2 transition-all cursor-pointer
                  ${
                    Number(orderDetails.orderType) === 1
                      ? 'border-blue-500 bg-blue-50 text-blue-600'
                      : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300'
                  }`}
              >
                <span className="text-[14px] font-semibold">Delivery</span>
                <span className="text-[12px] mt-1 font-normal">
                  {store?.flatDeliveryFee
                    ? formatCurrency(store.flatDeliveryFee)
                    : 'Varies'}
                </span>
              </button>
            )}
          </div>
        ) : (
          <p className="text-[12px] text-red-500 font-semibold">
            No order type available for this store
          </p>
        )}
        <ErrorText text={String(orderDetailsValid.orderType)} />
      </div>

      <div>
        {!user && (
          <div className={cardClass}>
            <h2 className="text-[15px] font-bold text-gray-800 mb-4">
              User Details
            </h2>
            <div className="mb-4">
              <label className={labelClass}>First Name</label>
              <input
                placeholder="First Name"
                className={inputClass}
                value={orderDetails.firstName}
                onChange={(e) =>
                  setOrderDetails({
                    ...orderDetails,
                    firstName: e.target.value,
                  })
                }
              />
              <ErrorText text={String(orderDetailsValid.firstName)} />
            </div>
            <div className="mb-4">
              <label className={labelClass}>Last Name</label>
              <input
                placeholder="Last Name"
                className={inputClass}
                value={orderDetails.lastName}
                onChange={(e) =>
                  setOrderDetails({
                    ...orderDetails,
                    lastName: e.target.value,
                  })
                }
              />
              <ErrorText text={String(orderDetailsValid.lastName)} />
            </div>
            <div className="mb-4">
              <label className={labelClass}>Phone number</label>
              <PhoneInput
                defaultCountry="NG"
                countries={['NG']}
                international
                countryCallingCodeEditable={false}
                limitMaxLength
                value={orderDetails.phoneNumber}
                onChange={(value) => {
                  setOrderDetails({
                    ...orderDetails,
                    phoneNumber: value || '',
                  });
                }}
                numberInputProps={{
                  name: 'phoneNumber',
                  className: inputClass,
                }}
              />
              <ErrorText text={String(orderDetailsValid.phoneNumber)} />
            </div>

            <div className="mb-4">
              <label className={labelClass}>Email address</label>
              <input
                placeholder="Email address"
                className={inputClass}
                value={orderDetails.emailAddress}
                onChange={(e) =>
                  setOrderDetails({
                    ...orderDetails,
                    emailAddress: e.target.value,
                  })
                }
              />
              <ErrorText text={String(orderDetailsValid.emailAddress)} />
            </div>
          </div>
        )}

        {/* Delivery Details */}
        {Number(orderDetails.orderType) === 1 && (
          <div className={cardClass}>
            <h2 className="text-[15px] font-bold text-gray-800 mb-4">
              Delivery Details
            </h2>

            <div className="mb-4">
              <label className={labelClass}>Delivery address</label>
              <div
                className="border border-gray-200 
              rounded-lg  
              text-[13px] bg-blue-200"
              >
                <AddressComponent onSelect={handleSelect} debounceDelay={800} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelClass}>City</label>
                <input
                  placeholder="City"
                  className={inputClass}
                  value={orderDetails.city}
                  onChange={(e) =>
                    setOrderDetails({ ...orderDetails, city: e.target.value })
                  }
                />
              </div>
              <div>
                <label className={labelClass}>Nearest landmark</label>
                <input
                  placeholder="Nearest landmark"
                  className={inputClass}
                  value={orderDetails.landmark}
                  onChange={(e) =>
                    setOrderDetails({
                      ...orderDetails,
                      landmark: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            {store &&
              store?.selfFulfilmentDeliveryProviderOptions.deliveryMode ===
                'LocationGroups' &&
              store?.deliveryOptions.deliveryProvider === 'SelfFulfilment' &&
              landmarks && (
                <div className="mb-4">
                  <label className={labelClass}>Delivery Location</label>
                  <select
                    className={inputClass}
                    onChange={(e) =>
                      setOrderDetails({
                        ...orderDetails,
                        deliveryLocationId: +e.target.value,
                      })
                    }
                    defaultValue={String(orderDetails.deliveryLocationId)}
                  >
                    <option value="">-- Select your delivery area --</option>
                    {landmarks.map((thislandmark, index) => (
                      <option key={index} value={thislandmark.id}>
                        {thislandmark.area}
                      </option>
                    ))}
                  </select>
                </div>
              )}
          </div>
        )}
      </div>
      {/* Order Note */}
      <div className={cardClass}>
        <h2 className="text-[15px] font-bold text-gray-800 mb-3">
          Order note (optional)
        </h2>
        <textarea
          placeholder="Any special instructions for your order..."
          rows={4}
          className={`${inputClass} resize-none`}
          value={orderDetails.deliveryInstructions}
          onChange={(e) =>
            setOrderDetails({
              ...orderDetails,
              deliveryInstructions: e.target.value,
            })
          }
        />
        <ErrorText text={String(orderDetailsValid.deliveryInstructions)} />
      </div>
    </div>
  );
}
